import * as React from 'react'

import { IReactionDisposer, reaction } from 'mobx'
import { inject, observer } from 'mobx-react'

import ChatScreens from '~/client/src/shared/chat2/components/ChatScreens'
import WorkflowCardDataStore from '~/client/src/shared/chat2/components/workflow/WorkflowCardData.store'
import { ChatShellStore } from '~/client/src/shared/chat2/web/ChatShell.store'
import ChatService from '~/client/src/shared/services/ChatService/Chat.service'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import BottomNavigationNavBar from '../../components/BottomNavigationNavBar/BottomNavigationNavBar'
import MobileView from '../../components/MobileView'
import MobileWorkflowDrawer from '../../components/MobileWorkflowDrawer/MobileWorkflowDrawer'
import MobileInitialState from '../../stores/MobileInitialState'

interface IProps {
  state?: MobileInitialState
  struxHubAuthService?: StruxHubAuthService
  chatService?: ChatService
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  companiesStore?: CompaniesStore
  projectRolesStore?: ProjectRolesStore
  projectTeamsStore?: ProjectTeamsStore
  projectTradesStore?: ProjectTradesStore
  tagsStore?: TagsStore
  projectDateStore?: ProjectDateStore
  projectsStore?: ProjectsStore
  graphExecutorStore?: GraphExecutorStore
  permitTypesStore?: PermitTypesStore
  locationAttributesStore?: LocationAttributesStore
}

@inject(
  'state',
  'struxHubAuthService',
  'chatService',
  'projectMembersStore',
  'userProjectsStore',
  'companiesStore',
  'projectRolesStore',
  'projectTeamsStore',
  'projectTradesStore',
  'tagsStore',
  'projectDateStore',
  'projectsStore',
  'graphExecutorStore',
  'permitTypesStore',
  'locationAttributesStore',
)
@observer
export default class ChatV2View extends React.Component<IProps> {
  private readonly shell: ChatShellStore
  // Called from inside child observer renders, where reading this.props is
  // forbidden; the injected stores are stable, so they are captured here.
  private readonly createWorkflowCardStore: () => WorkflowCardDataStore
  private overlayDisposer: IReactionDisposer = null
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)
    this.shell = new ChatShellStore({
      state: props.state,
      struxHubAuthService: props.struxHubAuthService,
      chatService: props.chatService,
      projectMembersStore: props.projectMembersStore,
      userProjectsStore: props.userProjectsStore,
      companiesStore: props.companiesStore,
      projectRolesStore: props.projectRolesStore,
    })
    this.createWorkflowCardStore = () =>
      new WorkflowCardDataStore(
        props.state,
        props.projectsStore,
        props.graphExecutorStore,
        props.projectDateStore,
        props.permitTypesStore,
        props.companiesStore,
        props.locationAttributesStore,
      )
  }

  public componentDidMount() {
    this.shell.mount()

    // The get-the-app banner and similar overlays only belong on the inbox;
    // any inner screen counts as an open overlay.
    this.overlayDisposer = reaction(
      () => this.shell.screen.kind,
      kind => {
        if (kind === 'inbox') {
          this.closeOverlay?.()
          this.closeOverlay = null
        } else if (!this.closeOverlay) {
          this.closeOverlay = notifyOverlayOpen()
        }
      },
    )
  }

  public componentWillUnmount() {
    this.shell.unmount()
    this.overlayDisposer?.()
    this.closeOverlay?.()
    this.props.state.resetWorkflowIdDisplayedOnGlobalCard()
  }

  public render() {
    return (
      <>
        <MobileView
          content={
            <ChatScreens
              shell={this.shell}
              projectDateStore={this.props.projectDateStore}
              selectorStores={{
                state: this.props.state,
                projectMembersStore: this.props.projectMembersStore,
                userProjectsStore: this.props.userProjectsStore,
                companiesStore: this.props.companiesStore,
                projectRolesStore: this.props.projectRolesStore,
                projectTeamsStore: this.props.projectTeamsStore,
                projectTradesStore: this.props.projectTradesStore,
                tagsStore: this.props.tagsStore,
              }}
              createWorkflowCardStore={this.createWorkflowCardStore}
            />
          }
          footer={
            this.shell.screen.kind === 'inbox' ? (
              <BottomNavigationNavBar />
            ) : null
          }
        />
        <MobileWorkflowDrawer />
      </>
    )
  }
}
